import React, { Component } from "react";
import { ReactTabulator } from "react-tabulator";
import "react-tabulator/lib/styles.css";
import "react-tabulator/lib/css/tabulator.min.css";
import FilePropertiesImage from "../Images/FileProperties.svg";
import {
  DownloadIndexSvg,
  EmailSvgImage,
  RenameFileSvg,
  RenameFileSaveSvg,
  RenameFileCancelSvg,
  ExpungementSvg,
  UploadSvgImage,
} from "../Svg/allsvg";

/**
 * The results grid shared by Index, Text and Semantic search: the same Tabulator table and the same toolbar the
 * previous screen had (show/hide columns, expunge, rename, email, download index, upload), now driven by props.
 * The parent keeps the imperative Tabulator calls it already relied on through `grid.table`.
 */
export default class ResultsGrid extends Component {
  tabulatorRef = null;

  options = {
    pagination: "local",
    paginationSize: 20,
    placeholder: "No Files Available",
    downloadReady: (fileContents, blob) => blob,
    // Tabulator is built asynchronously by react-tabulator: the columns go in once it exists.
    tableBuilt: () => this.applyColumns(),
  };

  get table() {
    return this.tabulatorRef && this.tabulatorRef.table
      ? this.tabulatorRef.table
      : null;
  }

  componentDidMount() {
    this.applyColumns();
  }

  componentDidUpdate(previousProps) {
    if (previousProps.columnsKey !== this.props.columnsKey) {
      this.applyColumns();
    }
    if (previousProps.data !== this.props.data) {
      // Tabulator's own data sync runs from props; rows with wrapped text are re-measured and the footer follows.
      setTimeout(() => {
        const table = this.table;
        if (table) {
          try {
            table.redraw(true);
          } catch (ignored) {}
        }
        this.setFooterText(1);
      }, 0);
    }
  }

  // Columns carry formatter functions, so they are applied imperatively (as before) instead of through props,
  // which Tabulator compares by value.
  applyColumns = () => {
    const table = this.table;
    if (!table || !Array.isArray(this.props.columns)) return;
    try {
      table.setColumns(this.props.columns);
      if (Array.isArray(this.props.data)) table.setData(this.props.data);
      this.setFooterText(1);
    } catch (error) {
      // Tabulator may not be built yet on the very first paint; the next update applies the columns.
      setTimeout(() => {
        const late = this.table;
        if (late) {
          try {
            late.setColumns(this.props.columns);
            if (Array.isArray(this.props.data)) late.setData(this.props.data);
            this.setFooterText(1);
          } catch (ignored) {}
        }
      }, 50);
    }
  };

  setFooterText = (pageNumber, rowCount) => {
    const span = document.getElementById("resultsCount");
    const table = this.table;
    if (!span) return;
    const data = Array.isArray(this.props.data) ? this.props.data : [];
    const total = rowCount == null ? data.length : rowCount;
    if (total < 1 || !table) {
      span.innerText = "";
      return;
    }
    let pageSize = 20;
    try {
      pageSize = table.getPageSize() || 20;
    } catch (ignored) {}
    const first = (pageNumber - 1) * pageSize + 1;
    const last = Math.min(pageNumber * pageSize, total);
    span.innerText =
      total > pageSize
        ? `Result count: ${first} - ${last} (of ${total})`
        : `Result count: ${last}`;
  };

  getSelectedData = () => {
    const table = this.table;
    try {
      return table ? table.getSelectedData() : [];
    } catch (ignored) {
      return [];
    }
  };

  renderToolbar() {
    const { toolbar, renameMode, summary } = this.props;
    const actions = toolbar || {};
    return (
      <div className="ds-grid-toolbar">
        <div className="ds-grid-toolbar-left">
          <button
            type="button"
            className="ds-icon-button ds-icon-button-outlined"
            title="Show/Hide Columns"
            aria-label="Show or hide columns"
            onClick={actions.onColumns}
            disabled={!actions.onColumns}
          >
            <img src={FilePropertiesImage} alt="" aria-hidden="true" />
          </button>
          {summary && <span className="ds-grid-summary">{summary}</span>}
        </div>
        {renameMode ? (
          <div className="ds-grid-actions ds-grid-actions-rename" role="toolbar" aria-label="Rename files">
            <button type="button" className="ds-icon-button" title="Save" aria-label="Save file names" onClick={actions.onRenameSave}>
              <RenameFileSaveSvg />
            </button>
            <button type="button" className="ds-icon-button" title="Cancel" aria-label="Cancel renaming" onClick={actions.onRenameCancel}>
              <RenameFileCancelSvg />
            </button>
          </div>
        ) : (
          <div className="ds-grid-actions" role="toolbar" aria-label="Actions on selected files">
            {actions.showExpunge && (
              <button type="button" className="ds-icon-button" title="Expunge Records" aria-label="Expunge records" onClick={actions.onExpunge}>
                <ExpungementSvg />
              </button>
            )}
            {actions.showRename && (
              <button type="button" className="ds-icon-button" title="Rename Selected" aria-label="Rename selected" onClick={actions.onRename}>
                <RenameFileSvg />
              </button>
            )}
            {actions.showEmail && (
              <button type="button" className="ds-icon-button" title="Email Selected" aria-label="Email selected" onClick={actions.onEmail}>
                <EmailSvgImage />
              </button>
            )}
            {actions.showDownload && (
              <button type="button" className="ds-icon-button" title="Download Index Data" aria-label="Download index data" onClick={actions.onDownload}>
                <DownloadIndexSvg />
              </button>
            )}
            {actions.showUpload && (
              <button type="button" className="ds-icon-button" title="Upload Files" aria-label="Upload files (append or prepend)" onClick={actions.onUpload}>
                <UploadSvgImage />
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  render() {
    const { data, index } = this.props;
    return (
      <section className="ds-results" aria-label="Search results">
        {this.renderToolbar()}
        <div className="ds-grid">
          <ReactTabulator
            id="table"
            columns={[]}
            data={Array.isArray(data) ? data : []}
            index={index || "FileId"}
            // Phones keep natural column widths and scroll sideways instead of squeezing every column.
            layout={typeof window !== "undefined" && window.innerWidth <= 767 ? "fitDataStretch" : "fitColumns"}
            ref={(ref) => (this.tabulatorRef = ref)}
            options={this.options}
            footerElement={<span id="resultsCount" style={{ float: "left" }} />}
            pageLoaded={(pageNumber) => this.setFooterText(pageNumber)}
          />
        </div>
      </section>
    );
  }
}
